import { Check, ChevronDown, ChevronUp, Code2, Copy, WrapText } from 'lucide-react';
import { memo, useEffect, useId, useMemo, useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { highlightCodeLines } from '@/shared/lib/code-highlight';

interface MioCodeBlockProps {
  code: string;
  language?: string | undefined;
  title?: string | undefined;
}

/** A selectable code example, shared by streamed Markdown and Mio's visual tools. */
export const MioCodeBlock = memo(function MioCodeBlock({
  code,
  language = 'text',
  title,
}: MioCodeBlockProps) {
  const id = useId();
  const normalizedLanguage = language.trim().toLowerCase() || 'text';
  const lines = useMemo(
    () => highlightCodeLines(code, normalizedLanguage),
    [code, normalizedLanguage],
  );
  const [wrapped, setWrapped] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const long = lines.length > 12;
  const label = title ?? `${normalizedLanguage === 'text' ? 'Code' : normalizedLanguage} example`;

  useEffect(() => {
    setCopyStatus('idle');
  }, [code]);
  useEffect(() => {
    if (copyStatus !== 'copied') return;
    const timer = window.setTimeout(() => setCopyStatus('idle'), 2000);
    return () => window.clearTimeout(timer);
  }, [copyStatus]);

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(code);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
  }

  return (
    <div className="mio-code-block border-line bg-surface-raised my-3 min-w-0 max-w-full overflow-hidden rounded-lg border">
      <div className="border-line bg-surface flex min-w-0 items-center gap-2 border-b px-3 py-2">
        <Code2 className="text-fg-muted size-3.5 shrink-0" aria-hidden="true" />
        <span
          id={`${id}-title`}
          className="text-fg min-w-0 flex-1 truncate text-xs font-medium"
          title={label}
        >
          {title ?? normalizedLanguage}
        </span>
        <button
          type="button"
          onClick={() => setWrapped((value) => !value)}
          aria-label="Wrap code lines"
          aria-pressed={wrapped}
          aria-controls={`${id}-code`}
          title="Wrap code lines"
          className={cn(
            'mio-code-control min-w-11 justify-center lg:min-w-8',
            wrapped && 'bg-surface-raised text-fg',
          )}
        >
          <WrapText size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={copyCode}
          aria-label="Copy code"
          title="Copy code"
          className="mio-code-control min-w-11 justify-center lg:min-w-8"
        >
          {copyStatus === 'copied' ? (
            <Check size={14} aria-hidden="true" />
          ) : (
            <Copy size={14} aria-hidden="true" />
          )}
          <span>{copyStatus === 'copied' ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      <pre
        id={`${id}-code`}
        className={cn('mio-code-scroll', expanded ? 'max-h-[65vh]' : 'max-h-72')}
        data-wrapped={wrapped}
        role="region"
        aria-label={label}
        tabIndex={0}
      >
        <code className="mio-code-source">
          {lines.map((tokens, index) => (
            <span key={index} className="mio-code-line">
              <span className="mio-code-number" aria-hidden="true" data-line={index + 1} />
              <span className="mio-code-content">
                {tokens.map((token, tokenIndex) => (
                  <span key={tokenIndex} className={token.className}>
                    {token.text}
                  </span>
                ))}
                {index < lines.length - 1 ? '\n' : null}
              </span>
            </span>
          ))}
        </code>
      </pre>
      <div className="border-line text-fg-subtle flex items-center justify-between gap-2 border-t px-3 py-1.5 text-xs">
        <span>
          {title ? `${normalizedLanguage} · ` : ''}
          {lines.length} {lines.length === 1 ? 'line' : 'lines'}
        </span>
        {long ? (
          <button
            type="button"
            className="mio-code-control"
            aria-label={expanded ? 'Collapse code view' : 'Expand code view'}
            aria-expanded={expanded}
            aria-controls={`${id}-code`}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? (
              <ChevronUp size={12} aria-hidden="true" />
            ) : (
              <ChevronDown size={12} aria-hidden="true" />
            )}
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        ) : null}
        <span role="status" className={copyStatus === 'failed' ? 'text-danger' : 'sr-only'}>
          {copyStatus === 'copied'
            ? 'Code copied to clipboard.'
            : copyStatus === 'failed'
              ? 'Copy unavailable. Select the code to copy.'
              : ''}
        </span>
      </div>
    </div>
  );
});
